import React, { useMemo, useState } from 'react';
import { Building2, User, Crosshair, TrendingUp, ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';

function TreeNode({ icon: Icon, label, sublabel, badge, badgeClass, defaultOpen = true, children, indent = 0 }) {
  const [open, setOpen] = useState(defaultOpen);
  const hasChildren = !!children;
  return (
    <div style={{ marginLeft: indent }}>
      <div className="flex items-center gap-2 py-1.5">
        {hasChildren ? (
          <button onClick={() => setOpen(o => !o)} className="text-[#706E6B] hover:text-[#0176D3] shrink-0">
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : <span className="w-[14px] shrink-0" />}
        <Icon size={14} className="text-[#706E6B] shrink-0" />
        <span className="text-[13px] font-bold text-[#080707]">{label}</span>
        {sublabel && <span className="text-[11px] text-[#706E6B]">{sublabel}</span>}
        {badge && <span className={cn('sf-badge', badgeClass || 'sf-badge-neutral')}>{badge}</span>}
      </div>
      {hasChildren && open && (
        <div className="border-l border-[#D8DDE6] ml-[6px] pl-3">{children}</div>
      )}
    </div>
  );
}

/**
 * Two sub-views, toggled by `mode`:
 * - "relationships": Account -> CrmContact -> Leads/Deals (via crmContactId),
 *   plus an "Unlinked" bucket for Leads/Deals that only have accountId set.
 * - "orgChart": recursive contacts tree by reportsToId.
 */
export default function AccountHierarchyTree({ mode, account, contacts, leads, deals }) {
  const relationshipTree = useMemo(() => {
    const leadsByContact = {}; const dealsByContact = {};
    const unlinkedLeads = []; const unlinkedDeals = [];
    leads.forEach(l => {
      if (l.crmContactId) (leadsByContact[l.crmContactId] ||= []).push(l);
      else unlinkedLeads.push(l);
    });
    deals.forEach(d => {
      if (d.crmContactId) (dealsByContact[d.crmContactId] ||= []).push(d);
      else unlinkedDeals.push(d);
    });
    return { leadsByContact, dealsByContact, unlinkedLeads, unlinkedDeals };
  }, [leads, deals]);

  const orgChartRoots = useMemo(() => {
    const byId = {};
    contacts.forEach(c => { byId[c._id] = { ...c, children: [] }; });
    const roots = [];
    contacts.forEach(c => {
      if (c.reportsToId && byId[c.reportsToId]) byId[c.reportsToId].children.push(byId[c._id]);
      else roots.push(byId[c._id]);
    });
    return roots;
  }, [contacts]);

  if (mode === 'orgChart') {
    if (contacts.length === 0) {
      return <p className="text-[12px] text-[#706E6B] py-8 text-center">No contacts on this account yet.</p>;
    }
    const renderOrgNode = (node) => (
      <TreeNode key={node._id} icon={User} label={node.name} sublabel={node.designation}>
        {node.children.length > 0 ? node.children.map(renderOrgNode) : undefined}
      </TreeNode>
    );
    return <div className="sf-card p-4">{orgChartRoots.map(renderOrgNode)}</div>;
  }

  const { leadsByContact, dealsByContact, unlinkedLeads, unlinkedDeals } = relationshipTree;
  return (
    <div className="sf-card p-4">
      <TreeNode icon={Building2} label={account.name} badge="Account" badgeClass="sf-badge-manager">
        {contacts.map(c => (
          <TreeNode key={c._id} icon={User} label={c.name} sublabel={c.designation}>
            {((leadsByContact[c._id]?.length || 0) + (dealsByContact[c._id]?.length || 0)) > 0 ? (
              <>
                {(leadsByContact[c._id] || []).map(l => (
                  <TreeNode key={l._id} icon={Crosshair} label={l.name} badge={l.status} badgeClass="sf-badge-warning" defaultOpen={false} />
                ))}
                {(dealsByContact[c._id] || []).map(d => (
                  <TreeNode key={d._id} icon={TrendingUp} label={d.title} badge={d.stage} badgeClass="sf-badge-success" defaultOpen={false} />
                ))}
              </>
            ) : undefined}
          </TreeNode>
        ))}
        {(unlinkedLeads.length + unlinkedDeals.length) > 0 && (
          <TreeNode icon={Building2} label="Unlinked to a contact" sublabel="(accountId set, no crmContactId)">
            {unlinkedLeads.map(l => (
              <TreeNode key={l._id} icon={Crosshair} label={l.name} badge={l.status} badgeClass="sf-badge-warning" defaultOpen={false} />
            ))}
            {unlinkedDeals.map(d => (
              <TreeNode key={d._id} icon={TrendingUp} label={d.title} badge={d.stage} badgeClass="sf-badge-success" defaultOpen={false} />
            ))}
          </TreeNode>
        )}
      </TreeNode>
    </div>
  );
}
